import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Loader2 } from "lucide-react";
import { RootState, AppDispatch } from "@/store/store";
import { getCurrentUser } from "@/store/slices/authSlice";
import { STAFF_USER } from "@/auth/capabilities";
import { UserRoles } from "@/types/auth";
import { useMockAuth } from "@/mocks/mockData";
import ErrorView from "@/components/error/ErrorView";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: readonly UserRoles[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles = STAFF_USER }) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { isAuthenticated, user, loading, sessionChecked } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    if (useMockAuth) return;
    if (!sessionChecked) {
      dispatch(getCurrentUser());
    }
  }, [sessionChecked, dispatch]);

  if (!sessionChecked || loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-label="Loading" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!(allowedRoles as readonly UserRoles[]).includes(user.role)) {
    return <ErrorView />;
  }

  if (!user.is_active) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
